import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Truck, Lock, Mail, Loader2, Layers, ScanLine, ReceiptText, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FEATURES = [
  { icon: Layers, title: "Multi-hub dispatch & receive", sub: "Every location, one command center" },
  { icon: ScanLine, title: "AI OCR for bills & FASTag", sub: "Scan a document, skip the typing" },
  { icon: ReceiptText, title: "GST, TDS & retention tracking", sub: "Ledgers that reconcile themselves" },
];

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Left brand hero */}
      <div className="relative hidden lg:flex w-[46%] xl:w-1/2 flex-col justify-between overflow-hidden bg-[hsl(var(--sidebar))] p-12">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, hsl(var(--sidebar))/0.6, hsl(var(--sidebar)) 65%)" }} />
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--primary)/0.5) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)/0.5) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="pointer-events-none absolute -top-24 -left-16 h-96 w-96 rounded-full bg-primary/30 blur-[130px]" />

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10">
          <div className="inline-flex rounded-2xl bg-white p-4 shadow-2xl shadow-black/30 ring-1 ring-white/10">
            <img src="/trucklink-logo.png" alt="TruckLink Accounting Platform by Swastik" className="h-40 w-40 xl:h-48 xl:w-48 object-contain" data-testid="login-hero-logo" />
          </div>
        </motion.div>

        <div className="relative z-10 max-w-md">
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }} className="font-display text-4xl xl:text-5xl font-bold leading-tight text-white">
            Run your fleet<br />like a <span className="text-primary">command center.</span>
          </motion.h1>
          <p className="mt-4 text-slate-300/90 text-base leading-relaxed">
            Freight accounting built for Indian trucking businesses — dispatch to reconciliation, in one place.
          </p>
          <div className="mt-9 space-y-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
                className="flex items-start gap-3.5"
              >
                <div className="h-10 w-10 shrink-0 rounded-xl bg-white/5 border border-white/10 backdrop-blur flex items-center justify-center text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{f.title}</div>
                  <div className="text-xs text-slate-400">{f.sub}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-500 font-mono">v2.0 · Shree Krishna Enterprises</div>
      </div>

      {/* Right form */}
      <div className="grid-bg flex-1 flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          className="w-full max-w-md"
          data-testid="login-card"
        >
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/trucklink-logo.png" alt="TruckLink Accounting Platform by Swastik" className="h-28 w-28 object-contain" data-testid="login-mobile-logo" />
          </div>

          <div className="hidden lg:flex justify-center mb-6">
            <img src="/trucklink-logo.png" alt="TruckLink Accounting Platform by Swastik" className="h-32 w-32 object-contain" data-testid="login-form-logo" />
          </div>

          <h2 className="font-display text-3xl font-bold tracking-tight text-center lg:text-left">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1.5 mb-8 text-center lg:text-left">Access your company workspace</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-10 h-11" type="email" placeholder="you@company.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-10 h-11" type="password" placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
              </div>
            </div>
            <Button type="submit" className="w-full h-11 text-sm font-semibold group" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Sign in <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" /></>)}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
